import type { IAISeatProfile } from '@core/table-ai';
import { baselineDecision, decisionSeed } from './decision';
import type { IAIObservation } from './observation';

export interface IAIDecisionRequest {
  readonly taskId: string; // session / round / decision，以執行器產生。
  readonly observation: IAIObservation;
  readonly profile: IAISeatProfile;
  readonly proxy: boolean;
}
export interface IAIWorker {
  onmessage: ((event: MessageEvent) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  postMessage(request: IAIDecisionRequest): void;
  terminate(): void;
}
export interface IAIPendingDecision {
  readonly result: Promise<number | null>;
  cancel(): void;
}

export interface IAIDiagnostic {
  readonly taskId: string;
  readonly elapsedMs: number;
  readonly fallbackReason: string | null;
  readonly cancelled: boolean;
  readonly truncated: boolean;
}
const diagnostics: IAIDiagnostic[] = []; // 僅留耗時與狀態，不保存任何私有觀察。
export const readAIDiagnostics = (): readonly IAIDiagnostic[] => structuredClone(diagnostics);

/** 每筆任務獨立 Worker；取消即銷毀，不能保留另一家私有觀察。 */
export const requestDecision = (
  request: IAIDecisionRequest,
  factory: () => IAIWorker = () =>
    new Worker(new URL('./decision-worker.ts', import.meta.url), {
      type: 'module',
    }),
  timeoutMs = 5000
): IAIPendingDecision => {
  const started = performance.now();
  let resolve!: (index: number | null) => void;
  const result = new Promise<number | null>((done) => {
    resolve = done;
  });
  let settled = false;
  let worker: IAIWorker | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let best: number | null = null;
  let fallbackReason: string | null = null;
  let truncated = false;
  const finish = (index: number | null): void => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    worker?.terminate();
    diagnostics.push({
      taskId: request.taskId,
      elapsedMs: performance.now() - started,
      fallbackReason,
      cancelled: index === null,
      truncated,
    });
    if (diagnostics.length > 512) diagnostics.shift();
    resolve(index);
  };
  const fallback = (reason: string): void => {
    if (settled) return;
    fallbackReason = reason;
    console.warn('[AI fallback]', {
      taskId: request.taskId,
      reason,
      hasPartial: best !== null,
    });
    finish(
      best ??
        baselineDecision(
          request.observation,
          decisionSeed(request.observation, request.profile.seed)
        )
    );
  };
  try {
    worker = factory();
    worker.onmessage = (event) => {
      if (settled) return;
      const message: unknown = event.data;
      if (typeof message !== 'object' || message === null) {
        fallback('invalid-message');
        return;
      }
      const data = message as {
        taskId?: unknown;
        index?: unknown;
        done?: unknown;
        metrics?: { truncated?: boolean };
      };
      if (data.taskId !== request.taskId) return;
      if (
        !Number.isInteger(data.index) ||
        typeof data.index !== 'number' ||
        !request.observation.candidates[data.index] ||
        typeof data.done !== 'boolean'
      ) {
        fallback('invalid-candidate');
        return;
      }
      best = data.index;
      if (data.done) {
        truncated = data.metrics?.truncated === true;
        finish(best);
      }
    };
    worker.onerror = () => fallback('worker-error');
    timer = setTimeout(() => fallback('deadline'), timeoutMs);
    worker.postMessage(request);
  } catch {
    fallback('worker-unavailable');
  }
  return { result, cancel: () => finish(null) };
};
